// Shared Notary knowledge base — injected into province × notary pages
// (Wave 6 of Notary Deep Upgrade). Single source of truth avoids URL bloat
// while giving Google + AI 20 doc types, country matrix, and priority tiers
// on every /provinces/<x>/notary-public page.

export interface NotaryDocType {
  th: string;
  en: string;
  note: string;
}

export const NOTARY_DOC_TYPES_20: NotaryDocType[] = [
  { th: "หนังสือมอบอำนาจ (POA)", en: "Power of Attorney", note: "ขาย/โอนที่ดิน, ปิดบัญชี, ยื่นภาษีต่างประเทศ — ต้อง Durable clause" },
  { th: "Affidavit of Support (I-134/I-864)", en: "Affidavit of Support", note: "US Visa/Green Card — Poverty Line 125%" },
  { th: "Affidavit of Single Status", en: "Single Status Affidavit", note: "สมรสต่างประเทศ (เสริม CNI)" },
  { th: "Affidavit of Identity", en: "Identity Affidavit", note: "พาสปอร์ตหาย, ชื่อสะกดผิด" },
  { th: "Affidavit of Domicile", en: "Domicile Affidavit", note: "US Probate — จัดการมรดก" },
  { th: "Statutory Declaration (UK/AU/CA/NZ)", en: "Statutory Declaration", note: "Change of Name / Common-Law / Lost Doc" },
  { th: "Deed Poll (UK)", en: "Deed Poll", note: "เปลี่ยนชื่อทางกฎหมาย — HMPO/DVLA" },
  { th: "Life Certificate", en: "Proof of Life / Life Certificate", note: "DWP UK / SSA US / DRV Germany — Pension" },
  { th: "Certified True Copy", en: "Certified True Copy", note: "Passport, บัตร ปชช., Diploma — ยื่นต่างประเทศ" },
  { th: "Bill of Sale (เรือ/รถ)", en: "Bill of Sale (Vessel/Vehicle)", note: "โอนเรือยอชต์/รถหรู ระหว่างประเทศ" },
  { th: "Board Resolution", en: "Corporate Board Resolution", note: "เปิดสาขา/แต่งตั้งผู้จัดการ ต่างประเทศ" },
  { th: "Commercial Invoice / Export Declaration", en: "Commercial Invoice Notarisation", note: "ส่งออก + Consular legalisation" },
  { th: "Diploma / Transcript", en: "Academic Credentials", note: "ยื่นเรียนต่อ, WES, ECE, NACES" },
  { th: "Marriage Certificate (ต่างประเทศ)", en: "Foreign Marriage Certificate", note: "ใช้ยื่นในไทย + สถานทูต" },
  { th: "Divorce Decree", en: "Divorce Decree Notarisation", note: "สมรสใหม่ต่างประเทศ" },
  { th: "Will & Testament", en: "Will / Codicil", note: "Thai will + foreign will coordination" },
  { th: "KYC (Wise/Revolut/N26)", en: "KYC Attestation", note: "Fintech + Crypto exchanges — Address + ID" },
  { th: "Trademark Assignment", en: "Trademark / IP Assignment", note: "WIPO Madrid + USPTO" },
  { th: "Contract / Agreement Signature", en: "Contract Signature Witnessing", note: "Employment, NDA, JV Agreement" },
  { th: "Lost Passport Declaration", en: "Lost Passport Affidavit", note: "สถานทูตออก Emergency Travel Doc" },
];

export interface CountryRecognition {
  code: string;
  name: string;
  chain: "apostille" | "embassy" | "hybrid";
  note: string;
}

export const NOTARY_COUNTRY_MATRIX: CountryRecognition[] = [
  { code: "US", name: "United States", chain: "apostille", note: "Apostille ใช้ได้ตั้งแต่ 28 ก.พ. 2027 — ก่อนหน้านั้นต้องผ่านนิติกรณ์ MFA + สถานทูตสหรัฐฯ" },
  { code: "UK", name: "United Kingdom", chain: "apostille", note: "FCDO ยอมรับ Thai Apostille ตรง" },
  { code: "AU", name: "Australia", chain: "apostille", note: "DFAT ยอมรับ — Statutory Declaration ใช้ได้" },
  { code: "CA", name: "Canada", chain: "apostille", note: "GAC ยอมรับ (Jan 2024) — IRCC Common-Law SD" },
  { code: "NZ", name: "New Zealand", chain: "apostille", note: "DIA ยอมรับ" },
  { code: "DE", name: "Germany", chain: "apostille", note: "Sworn Translator (Beeidigter Übersetzer) + Apostille" },
  { code: "FR", name: "France", chain: "apostille", note: "Traducteur Assermenté + Apostille" },
  { code: "IT", name: "Italy", chain: "apostille", note: "Traduttore Giurato + Apostille" },
  { code: "ES", name: "Spain", chain: "apostille", note: "Traductor Jurado MAEC + Apostille" },
  { code: "NL", name: "Netherlands", chain: "apostille", note: "Beëdigd Vertaler Wbtv + Apostille" },
  { code: "CH", name: "Switzerland", chain: "hybrid", note: "Notar Kanton + Staatskanzlei Apostille (2 ชั้น)" },
  { code: "AT", name: "Austria", chain: "apostille", note: "Gerichtsdolmetscher + Apostille" },
  { code: "JP", name: "Japan", chain: "apostille", note: "MOFA ยอมรับ Thai Apostille" },
  { code: "KR", name: "South Korea", chain: "apostille", note: "MOFA + Sworn KR translation" },
  { code: "SG", name: "Singapore", chain: "apostille", note: "SAL Apostille — ยอมรับ Thai Apostille" },
  { code: "CN", name: "China", chain: "apostille", note: "MFA China ยอมรับ (Nov 2023) — Thai Apostille ตรง" },
  { code: "HK", name: "Hong Kong", chain: "apostille", note: "High Court Apostille — ยอมรับ Thai Apostille" },
  { code: "AE", name: "UAE", chain: "embassy", note: "ยังต้องผ่าน UAE Embassy Bangkok — ไม่ใช่ Apostille" },
  { code: "SA", name: "Saudi Arabia", chain: "embassy", note: "Saudi Embassy + Chamber of Commerce" },
  { code: "RU", name: "Russia", chain: "apostille", note: "MFA RF ยอมรับ Thai Apostille" },
  { code: "IN", name: "India", chain: "apostille", note: "MEA ยอมรับ Thai Apostille" },
  { code: "TW", name: "Taiwan", chain: "embassy", note: "TECO Bangkok Legalisation (ไม่ใช่ Apostille)" },
  { code: "VN", name: "Vietnam", chain: "embassy", note: "Vietnam Embassy Legalisation" },
  { code: "PH", name: "Philippines", chain: "apostille", note: "DFA Apostille — ยอมรับ Thai Apostille" },
  { code: "MY", name: "Malaysia", chain: "embassy", note: "Malaysia High Commission Legalisation" },
];

export interface PriorityTier {
  code: "P1" | "P2" | "P3" | "P4" | "P5";
  name: string;
  sla: string;
  useCase: string;
  surchargePct: string;
}

export const NOTARY_PRIORITY_TIERS: PriorityTier[] = [
  { code: "P1", name: "Rush 15 นาที (ที่ออฟฟิศ)", sla: "≤ 15 นาที", useCase: "เที่ยวบิน 3 ชม.ข้างหน้า, บอร์ดประชุมด่วน", surchargePct: "+100%" },
  { code: "P2", name: "Same-Day Express", sla: "≤ 4 ชม.", useCase: "ยื่นสถานทูตพรุ่งนี้, MFA รอบบ่าย", surchargePct: "+50%" },
  { code: "P3", name: "Next-Day", sla: "≤ 24 ชม.", useCase: "ยื่นภายในสัปดาห์", surchargePct: "+30%" },
  { code: "P4", name: "Standard 2–3 วัน", sla: "2–3 วันทำการ", useCase: "งานปกติ Draft 2 ภาษา + Notary", surchargePct: "รวมในราคาปกติ" },
  { code: "P5", name: "After-Hours / On-Site", sla: "ตามนัด (โรงพยาบาล/สนามบิน/โรงแรม)", useCase: "Bedside signing, Airport emergency, ผู้บริหารต่างประเทศ", surchargePct: "+80%" },
];